import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { Lang, TFunc, makeT, detectLang } from "./i18n";

export type Theme = "dark" | "light";

/** Colores de los gráficos (recharts necesita strings concretos) por tema. */
export interface Palette {
  grid: string;
  axis: string;
  tipBg: string;
  tipBorder: string;
  tipLabel: string;
  tipText: string;
  cursor: string;
}

const DARK: Palette = {
  grid: "#26324a",
  axis: "#8aa0c0",
  tipBg: "#131a26",
  tipBorder: "#26324a",
  tipLabel: "#8aa0c0",
  tipText: "#e2e8f0",
  cursor: "#ffffff",
};

const LIGHT: Palette = {
  grid: "#e6e1d8",
  axis: "#7b766b",
  tipBg: "#ffffff",
  tipBorder: "#e6e1d8",
  tipLabel: "#7b766b",
  tipText: "#1b1813",
  cursor: "#1b1813",
};

interface UI {
  theme: Theme;
  setTheme: (t: Theme) => void;
  lang: Lang;
  setLang: (l: Lang) => void;
  t: TFunc;
  palette: Palette;
  loc: string;
}

const Ctx = createContext<UI | null>(null);

export function UIProvider({ children }: { children: React.ReactNode }) {
  // El render del servidor/primer paint usa los valores por defecto (es / light)
  // para no romper la hidratación; tras montar leemos preferencias reales.
  const [theme, setThemeState] = useState<Theme>("light");
  const [lang, setLangState] = useState<Lang>("es");

  useEffect(() => {
    const savedTheme = localStorage.getItem("theme") as Theme | null;
    const savedLang = localStorage.getItem("lang") as Lang | null;
    if (savedTheme === "light" || savedTheme === "dark") setThemeState(savedTheme);
    setLangState(savedLang === "es" || savedLang === "en" ? savedLang : detectLang());
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
  }, [theme]);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const setTheme = (t: Theme) => {
    setThemeState(t);
    localStorage.setItem("theme", t);
  };
  const setLang = (l: Lang) => {
    setLangState(l);
    localStorage.setItem("lang", l);
  };

  const t = useMemo(() => makeT(lang), [lang]);
  const value: UI = {
    theme,
    setTheme,
    lang,
    setLang,
    t,
    palette: theme === "light" ? LIGHT : DARK,
    loc: lang === "es" ? "es-ES" : "en-GB",
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useUI(): UI {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useUI debe usarse dentro de UIProvider");
  return ctx;
}
